// @givmo/app-admin — company direct-giving Idempotency-Key signature (EP-WEB1). ★ MONEY-MOVING ★
//
// The direct-gift confirm CHARGES a company card or debits the company wallet via
// `POST /charities|etfs|fundraisers/{id}/donations` (funding_source="company"), each guarded by the
// backend `claim_idempotency` dependency — which de-dupes ONLY across retries carrying the SAME
// `Idempotency-Key`. An UNKEYED retry (or a fresh key on retry) mints a new server-side series and
// does NOT cross-retry-dedup, so a double-click / network retry would DOUBLE-CHARGE. A stable client
// key is the only thing preventing that.
//
// Series identity for a direct gift = the exact target (type + id) + the whole-dollar amount + the
// funding source (a specific card id, or the wallet) + the "Cover processing fees" tick (CAM-BE13:
// covering is a different gross). Changing ANY of these is a genuinely different charge → a fresh key
// (re-mint on edit). The generic lifecycle holder is shared from `@givmo/payments`; this file supplies
// the give-specific signature, a bound factory, and the page-owned series (the key AND the request it
// was minted for, CAM-R59b).

import type { ResolvedDisclosure } from '@givmo/disclosure';
import { IdempotencyKeyHolder } from '@givmo/payments';
import type { ChargeQuote } from '../../components/charge-quote';
import type { CompanyDonationBody, Funding, TargetType } from './give-decode';

/** A stable signature of the gift being charged; a change ends the current attempt-series. */
export interface GiveSignature {
  readonly targetType: TargetType;
  readonly targetId: string;
  /** Whole-dollar integer amount. */
  readonly amount: number;
  readonly funding: Funding;
  /** The "Cover processing fees" tick (CAM-BE13); absent or `false` is the unticked gift. */
  readonly coverFees?: boolean;
}

/**
 * Deterministic fingerprint of a gift: target + amount + funding instrument, then `|cover` when the
 * fees are covered — so an uncovered gift's fingerprint is byte-identical to before the choice
 * existed, and a covered gift never shares a key with an uncovered one.
 */
export function giveFingerprint(signature: GiveSignature): string {
  const fundingTag =
    signature.funding.kind === 'wallet' ? 'wallet' : `card:${signature.funding.cardId}`;
  const cover = signature.coverFees === true ? '|cover' : '';
  return `${signature.targetType}:${signature.targetId}|amount:${signature.amount}|funding:${fundingTag}${cover}`;
}

/**
 * Construct the direct-giving idempotency-key holder, bound to {@link giveFingerprint}. A React
 * component holds ONE holder for its lifetime (a `useRef`), so the key is stable across re-renders by
 * construction and re-mints only when the signature changes. `generate` is injectable for tests.
 */
export function createGiveIdempotencyKeyHolder(
  generate?: () => string,
): IdempotencyKeyHolder<GiveSignature> {
  return new IdempotencyKeyHolder<GiveSignature>(giveFingerprint, generate);
}

/**
 * One gift request exactly as sent, frozen when its charge starts (CAM-BE13, CAM-R59b): the wire body
 * — with its cover-fees choice, stated figures and disclosure echo — and what the dialog stated it
 * with. A retry, or the same gift re-entered after a maybe-charged close, resends THIS.
 */
export interface GiveRequest {
  /** The gift's series signature: target, amount, funding source and the row's tick. */
  readonly signature: GiveSignature;
  readonly body: CompanyDonationBody;
  /** The server's quote the request was stated from. */
  readonly quote: ChargeQuote;
  /** Whether the request covers the fees (the tick, or the company's own setting). */
  readonly covered: boolean;
  /** The company-payer body the request was stated under (its echo rides `body`). */
  readonly disclosure: ResolvedDisclosure;
}

/**
 * The direct-gift dialog's charge series, PAGE-OWNED (CAM-R59b): the idempotency key and the request
 * it was minted for, held above the dialog so both survive its unmount on close. A maybe-charged gift
 * re-entered after a close is found by its signature and resent as it was — the same body under the
 * same key — so the backend replays its outcome (or refuses a stale echo or price before charging),
 * instead of refusing a body that moved under the held key, which is all it can do with one (cut 7
 * review F2). Cleared on a confirmed success and on a definite no-charge.
 */
export class GiveSeries {
  readonly #holder: IdempotencyKeyHolder<GiveSignature>;
  #request: GiveRequest | null = null;

  constructor(generate?: () => string) {
    this.#holder = createGiveIdempotencyKeyHolder(generate);
  }

  /** The key for this request — minted, or reused for the same signature — held with the request. */
  keyFor(request: GiveRequest): string {
    const key = this.#holder.keyFor(request.signature);
    this.#request = request;
    return key;
  }

  /** The request held for this gift and tick, or `null` (none held, or another gift's). */
  heldFor(signature: GiveSignature): GiveRequest | null {
    const held = this.#request;
    return held !== null && giveFingerprint(held.signature) === giveFingerprint(signature)
      ? held
      : null;
  }

  /** The tick of the request held for this gift (target + amount + funding), or `null`. */
  heldChoiceFor(gift: Omit<GiveSignature, 'coverFees'>): boolean | null {
    const held = this.#request;
    if (held === null) return null;
    const { coverFees = false, ...heldGift } = held.signature;
    return giveFingerprint(heldGift) === giveFingerprint(gift) ? coverFees : null;
  }

  /** The current key without minting (a test/inspection seam), or `null`. */
  get current(): string | null {
    return this.#holder.current;
  }

  /** Drop the key and its request: the next gift starts a new series. */
  clear(): void {
    this.#holder.clear();
    this.#request = null;
  }
}

/** Construct the page-owned {@link GiveSeries}. `generate` is injectable for tests. */
export function createGiveSeries(generate?: () => string): GiveSeries {
  return new GiveSeries(generate);
}
